// TrackFiji — Route Trace Loader (spec: TrackFiji_RouteLoader_Spec_R1.1, 11 Oct 2026)
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import "./route-loader.css";

export type LoaderKind = "history" | "report";
export type LoaderPhase = LoaderKind | "ready";

const ROUTE_D = "M20 92 C 45 45, 80 40, 100 65 S 145 105, 167 72 S 202 20, 240 42";
const PINGS = [0.17, 0.34, 0.5, 0.66, 0.83];
const HISTORY_HIT = [0.3, 0.37, 0.43, 0.48, 0.55]; // cycle fraction where the eased tip passes each ping
const REPORT_SEG = 0.3;                              // comet length as a fraction of the route
const EASE = "0.65 0 0.35 1";
const PIN_D = "M0 0 C -1.5 -4.5 -7 -7.5 -7 -13.5 A 7 7 0 1 1 7 -13.5 C 7 -7.5 1.5 -4.5 0 0 Z";

const COPY = {
  history: { title: "Loading history", sub: "Fetching vehicle positions", ready: "History loaded" },
  report: { title: "Building your report", sub: "Processing trips", ready: "Report ready" },
} as const;

interface Props {
  phase: LoaderPhase;
  kind: LoaderKind;               // which copy set to use (also decides the "ready" title)
  size?: "overlay" | "inline";
  title?: string;                 // override copy
  sub?: string;
  slowAfterMs?: number;           // when to show the "large ranges" hint
}

export function RouteLoader({ phase, kind, size = "overlay", title, sub, slowAfterMs = 8000 }: Props) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const routeId = `tf-route-${uid}`;
  const pathRef = useRef<SVGPathElement>(null);
  const [pts, setPts] = useState<[number, number][]>([]);
  const [slow, setSlow] = useState(false);
  const reduced = usePrefersReducedMotion();
  const anim = !reduced;

  useLayoutEffect(() => {
    const p = pathRef.current;
    if (!p || pts.length) return;
    const len = p.getTotalLength();
    setPts(PINGS.map((f) => { const q = p.getPointAtLength(len * f); return [q.x, q.y]; }));
  }, [phase, pts.length]);

  useEffect(() => {
    setSlow(false);
    if (phase === "ready") return;
    const id = window.setTimeout(() => setSlow(true), slowAfterMs);
    return () => window.clearTimeout(id);
  }, [phase, slowAfterMs]);

  const copy = COPY[kind];
  const heading = title ?? (phase === "ready" ? copy.ready : copy.title);
  const subline = sub ?? (phase === "ready" ? "" : copy.sub);

  return (
    <div className={`tf-loader tf-loader--${size}`} role="status" aria-live="polite">
      <svg key={phase} className="tf-loader__art" viewBox="0 0 260 120" aria-hidden="true">
        <path id={routeId} ref={pathRef} className="road" d={ROUTE_D} />

        {/* Trace */}
        {phase === "history" && (
          <path className="trace" d={ROUTE_D} pathLength={1} strokeDasharray="1 1" strokeDashoffset={anim ? 1 : 0}>
            {anim && <animate attributeName="stroke-dashoffset" values="1;0;0" keyTimes="0;0.85;1"
              calcMode="spline" keySplines={`${EASE};0 0 1 1`} dur="2.8s" repeatCount="indefinite" />}
          </path>
        )}
        {phase === "report" && (
          <path className="trace" d={ROUTE_D} pathLength={1}
            strokeDasharray={anim ? `${REPORT_SEG} 2` : undefined} strokeDashoffset={anim ? REPORT_SEG : 0}>
            {anim && <animate attributeName="stroke-dashoffset" values={`${REPORT_SEG};-1`} dur="1.5s" repeatCount="indefinite" />}
          </path>
        )}
        {phase === "ready" && <path className="trace" d={ROUTE_D} />}

        {/* Pings: hollow base + filled overlay that lights up */}
        {pts.map(([x, y], i) => {
          const p = PINGS[i];
          const lit = phase === "ready" || !anim;
          return (
            <g key={i}>
              <circle className="ping" cx={x} cy={y} r={4} />
              <circle className="ping-on" cx={x} cy={y} r={4} opacity={lit ? 1 : 0}>
                {anim && phase === "history" && (
                  <animate attributeName="opacity" values="0;1;0" keyTimes={`0;${HISTORY_HIT[i]};0.97`}
                    calcMode="discrete" dur="2.8s" repeatCount="indefinite" />
                )}
                {anim && phase === "report" && (
                  <animate attributeName="opacity" values="0;1;0"
                    keyTimes={`0;${(p / (1 + REPORT_SEG)).toFixed(3)};${((p + REPORT_SEG) / (1 + REPORT_SEG)).toFixed(3)}`}
                    calcMode="discrete" dur="1.5s" repeatCount="indefinite" />
                )}
              </circle>
            </g>
          );
        })}

        {/* Start pin */}
        <g transform="translate(20 92)"><path className="pin-body" d={PIN_D} /><circle className="pin-dot" cy={-13.5} r={2.6} /></g>

        {/* Destination pin: ghost while loading, drops in on ready */}
        {phase === "ready" && (
          <circle className="ring" cx={240} cy={42} r={3} opacity={0}>
            {anim && <>
              <animate attributeName="r" values="3;26" begin="0.4s" dur="0.8s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.3 1" keyTimes="0;1" />
              <animate attributeName="opacity" values="0.7;0" begin="0.4s" dur="0.8s" fill="freeze" />
            </>}
          </circle>
        )}
        <g transform="translate(240 42)">
          <g className={phase === "ready" ? "pin-end" : "pin-ghost"}>
            {anim && phase === "ready" && (
              <animateTransform attributeName="transform" type="translate" values="0 -24;0 1.5;0 0"
                keyTimes="0;0.7;1" calcMode="spline" keySplines="0.5 0 1 1;0 0 0.58 1" dur="0.45s" fill="freeze" />
            )}
            <path className="pin-body" d={PIN_D} /><circle className="pin-dot" cy={-13.5} r={2.6} />
          </g>
        </g>

        {/* Vehicle dot + halo riding the route */}
        {phase !== "ready" && (
          anim ? (
            <g>
              {phase === "history"
                ? <animateMotion dur="2.8s" repeatCount="indefinite" keyPoints="0;1;1" keyTimes="0;0.85;1"
                    calcMode="spline" keySplines={`${EASE};0 0 1 1`}><mpath href={`#${routeId}`} /></animateMotion>
                : <animateMotion dur="1.5s" repeatCount="indefinite" keyPoints="0;1;1" keyTimes="0;0.77;1"
                    calcMode="linear"><mpath href={`#${routeId}`} /></animateMotion>}
              {phase === "report" && <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.75;0.8;1" dur="1.5s" repeatCount="indefinite" />}
              <circle className="halo" r={5}>
                <animate attributeName="r" values="5;13" dur="1.2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0" dur="1.2s" repeatCount="indefinite" />
              </circle>
              <circle className="car" r={5} />
            </g>
          ) : <circle className="car" cx={240} cy={42} r={5} />
        )}
      </svg>

      <p className="tf-loader__title">{heading}</p>
      {subline && (
        <p className="tf-loader__sub">
          {subline}{phase !== "ready" && <span className="tf-loader__dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span>}
        </p>
      )}
      {slow && phase !== "ready" && <p className="tf-loader__hint">Large date ranges can take up to 15 seconds.</p>}
    </div>
  );
}

function usePrefersReducedMotion() {
  const q = "(prefers-reduced-motion: reduce)";
  const [r, setR] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setR(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return r;
}

/**
 * Drives the loader from a request's state.
 * - Shows `kind` only if loading lasts longer than delayMs (no flash on fast loads).
 * - On success, shows "ready" for readyMs, but only if the loader was visible.
 * - On error, hides at once (the caller shows its error UI).
 * Returns null when nothing should render.
 */
export function useLoaderPhase(loading: boolean, failed: boolean, kind: LoaderKind, delayMs = 300, readyMs = 700) {
  const [phase, setPhase] = useState<LoaderPhase | null>(null);
  const shown = useRef(false);
  useEffect(() => {
    if (loading) {
      const t = window.setTimeout(() => { shown.current = true; setPhase(kind); }, delayMs);
      return () => window.clearTimeout(t);
    }
    if (shown.current && !failed) {
      setPhase("ready");
      // reset the ref inside the timer, not here, so React StrictMode's dev re-run still shows "ready"
      const t = window.setTimeout(() => { shown.current = false; setPhase(null); }, readyMs);
      return () => window.clearTimeout(t);
    }
    shown.current = false;
    setPhase(null);
  }, [loading, failed, kind, delayMs, readyMs]);
  return phase;
}
